import { ManagedUserRoles } from "@redotech/account-common/roles";
import { PredefinedChipOptions } from "@redotech/redo-customer-widget-common/customer-widget";
import { disputesAccessOverrides } from "@redotech/redo-dispute-settings-common/reclaim-access-settings";
import { HoldStatusStrategyZod } from "@redotech/redo-order-editing-settings-common/hold-status-strategy";
import { OrderEditingDisallowEventZod } from "@redotech/redo-order-editing-settings-common/order-editing-disallow-event";
import {
  OrderEditingFilterSchema,
  OrderEditingRuleSchema,
} from "@redotech/redo-order-editing-settings-common/order-editing-settings";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { canDirectlyWriteSetting } from "../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingMetadata,
  SettingProductArea,
  SettingVisibility,
} from "../settings-catalog/types";
import { BrandKitFlatTeamSettingMetadataByKey } from "./brand-kit/catalog";
import { BrandKitFlatTeamSettingGroups } from "./brand-kit/groups";
import { BrandKitFlatTeamSettingsSchema } from "./brand-kit/settings";
import { ContextualFlatTeamSettingMetadataByKey } from "./contextual/catalog";
import { ContextualFlatTeamSettingGroups } from "./contextual/groups";
import { ContextualFlatTeamSettingsSchema } from "./contextual/settings";
import {
  CoverageFlatTeamSettingCatalogDefinitions,
  CoverageFlatTeamSettingMetadataByKey,
} from "./coverage/catalog";
import { CoverageFlatTeamSettingGroups } from "./coverage/groups";
import { CoverageFlatTeamSettingsSchema } from "./coverage/settings";
import {
  CustomerWidgetFlatTeamSettingCatalogDefinitions,
  CustomerWidgetFlatTeamSettingMetadataByKey,
} from "./customer-widget/catalog";
import { CustomerWidgetFlatTeamSettingGroups } from "./customer-widget/groups";
import {
  FulfillmentFlatTeamSettingCatalogDefinitions,
  FulfillmentFlatTeamSettingMetadataByKey,
} from "./fulfillment/catalog";
import { FulfillmentFlatTeamSettingGroups } from "./fulfillment/groups";
import {
  HsaFlatTeamSettingCatalogDefinitions,
  HsaFlatTeamSettingMetadataByKey,
} from "./hsa/catalog";
import { HsaFlatTeamSettingGroups } from "./hsa/groups";
import {
  InternationalCustomsFlatTeamSettingCatalogDefinitions,
  InternationalCustomsFlatTeamSettingMetadataByKey,
} from "./international-customs/catalog";
import { InternationalCustomsFlatTeamSettingGroups } from "./international-customs/groups";
import { InternationalCustomsFlatTeamSettingsSchema } from "./international-customs/settings";
import {
  InternationalReturnsFlatTeamSettingCatalogDefinitions,
  InternationalReturnsFlatTeamSettingMetadataByKey,
} from "./international-returns/catalog";
import { InternationalReturnsFlatTeamSettingGroups } from "./international-returns/groups";
import { InternationalReturnsFlatTeamSettingsSchema } from "./international-returns/settings";
import {
  InventoryGeneralFlatTeamSettingCatalogDefinitions,
  InventoryGeneralFlatTeamSettingMetadataByKey,
} from "./inventory-general/catalog";
import { InventoryGeneralFlatTeamSettingGroups } from "./inventory-general/groups";
import { InventoryGeneralFlatTeamSettingsSchema } from "./inventory-general/settings";
import {
  InventoryPurchaseOrdersFlatTeamSettingCatalogDefinitions,
  InventoryPurchaseOrdersFlatTeamSettingMetadataByKey,
} from "./inventory-purchase-orders/catalog";
import { InventoryPurchaseOrdersFlatTeamSettingGroups } from "./inventory-purchase-orders/groups";
import { InventoryPurchaseOrdersFlatTeamSettingsSchema } from "./inventory-purchase-orders/settings";
import {
  OrderTrackingSettingsFlatTeamSettingCatalogDefinitions,
  OrderTrackingSettingsFlatTeamSettingMetadataByKey,
} from "./order-tracking-settings/catalog";
import { OrderTrackingSettingsFlatTeamSettingGroups } from "./order-tracking-settings/groups";
import { OrderTrackingSettingsFlatTeamSettingsSchema } from "./order-tracking-settings/settings";
import {
  ProductsGeneralFlatTeamSettingCatalogDefinitions,
  ProductsGeneralFlatTeamSettingMetadataByKey,
} from "./products-general/catalog";
import { ProductsGeneralFlatTeamSettingGroups } from "./products-general/groups";
import { ProductsGeneralFlatTeamSettingsSchema } from "./products-general/settings";
import {
  ReturnsFlatTeamSettingCatalogDefinitions,
  ReturnsFlatTeamSettingMetadataByKey,
} from "./returns/catalog";
import { ReturnsFlatTeamSettingGroups } from "./returns/groups";
import { ReturnsFlatTeamSettingsSchema } from "./returns/settings";
import { createPrefixedSchema, ISODateString } from "./schema-utils";
import {
  ShippingCarriersFlatTeamSettingCatalogDefinitions,
  ShippingCarriersFlatTeamSettingMetadataByKey,
} from "./shipping-carriers/catalog";
import { ShippingCarriersFlatTeamSettingGroups } from "./shipping-carriers/groups";
import { ShippingCarriersFlatTeamSettingsSchema } from "./shipping-carriers/settings";
import {
  ShippingCrossBorderFlatTeamSettingCatalogDefinitions,
  ShippingCrossBorderFlatTeamSettingMetadataByKey,
} from "./shipping-cross-border/catalog";
import { ShippingCrossBorderFlatTeamSettingGroups } from "./shipping-cross-border/groups";
import {
  ShippingFulfillmentPromiseFlatTeamSettingCatalogDefinitions,
  ShippingFulfillmentPromiseFlatTeamSettingMetadataByKey,
} from "./shipping-fulfillment-promise/catalog";
import { ShippingFulfillmentPromiseFlatTeamSettingGroups } from "./shipping-fulfillment-promise/groups";
import { ShippingFulfillmentPromiseFlatTeamSettingsSchema } from "./shipping-fulfillment-promise/settings";
import {
  SupportFlatTeamSettingCatalogDefinitions,
  SupportFlatTeamSettingMetadataByKey,
} from "./support/catalog";
import { SupportFlatTeamSettingGroups } from "./support/groups";
import { SupportFlatTeamSettingsSchema } from "./support/settings";
import {
  WarrantyRegistrationRewardsFlatTeamSettingCatalogDefinitions,
  WarrantyRegistrationRewardsFlatTeamSettingMetadataByKey,
} from "./warranty-registration-rewards/catalog";
import { WarrantyRegistrationRewardsFlatTeamSettingGroups } from "./warranty-registration-rewards/groups";
import { WarrantyRegistrationRewardsFlatTeamSettingsSchema } from "./warranty-registration-rewards/settings";
import {
  WarrantyRegistrationFlatTeamSettingCatalogDefinitions,
  WarrantyRegistrationFlatTeamSettingMetadataByKey,
} from "./warranty-registration/catalog";
import { WarrantyRegistrationFlatTeamSettingGroups } from "./warranty-registration/groups";
import { WarrantyRegistrationFlatTeamSettingsSchema } from "./warranty-registration/settings";

import {
  CustomerAccountsAdBlocksFlatTeamSettingCatalogDefinitions,
  CustomerAccountsAdBlocksFlatTeamSettingMetadataByKey,
} from "./customer-accounts-ad-blocks/catalog";
import { CustomerAccountsAdBlocksFlatTeamSettingGroups } from "./customer-accounts-ad-blocks/groups";
import { CustomerAccountsAdBlocksFlatTeamSettingsSchema } from "./customer-accounts-ad-blocks/settings";
import {
  CustomerAccountsStorefrontDisplayFlatTeamSettingCatalogDefinitions,
  CustomerAccountsStorefrontDisplayFlatTeamSettingMetadataByKey,
} from "./customer-accounts-storefront-display/catalog";
import { CustomerAccountsStorefrontDisplayFlatTeamSettingGroups } from "./customer-accounts-storefront-display/groups";
import { CustomerAccountsStorefrontDisplayFlatTeamSettingsSchema } from "./customer-accounts-storefront-display/settings";
import {
  CustomerPortalBundlesFlatTeamSettingCatalogDefinitions,
  CustomerPortalBundlesFlatTeamSettingMetadataByKey,
} from "./customer-portal-bundles/catalog";
import { CustomerPortalBundlesFlatTeamSettingGroups } from "./customer-portal-bundles/groups";
import { CustomerPortalBundlesFlatTeamSettingsSchema } from "./customer-portal-bundles/settings";
import {
  CustomerPortalFlatTeamSettingCatalogDefinitions,
  CustomerPortalFlatTeamSettingMetadataByKey,
} from "./customer-portal/catalog";
import { CustomerPortalFlatTeamSettingGroups } from "./customer-portal/groups";
import { CustomerPortalFlatTeamSettingsSchema } from "./customer-portal/settings";
import {
  PortalBuilderFlatTeamSettingCatalogDefinitions,
  PortalBuilderFlatTeamSettingMetadataByKey,
} from "./portal-builder/catalog";
import { PortalBuilderFlatTeamSettingGroups } from "./portal-builder/groups";
import { PortalBuilderFlatTeamSettingsSchema } from "./portal-builder/settings";

export type { CoverageFlatTeamSettingGroupId } from "./coverage/groups";
export type { CustomerWidgetFlatTeamSettingGroupId } from "./customer-widget/groups";
export type { FulfillmentFlatTeamSettingGroupId } from "./fulfillment/groups";
export type { HsaFlatTeamSettingGroupId } from "./hsa/groups";
export type { ReturnsFlatTeamSettingGroupId } from "./returns/groups";
export type { SupportFlatTeamSettingGroupId } from "./support/groups";

export type NOOP<T> = { [K in keyof T]: T[K] } & {};

/**
 * Flat Zod schema for team settings based on the flattened structure.
 * This schema represents settings paths like "support/ai/enabled" and "concierge/general/name"
 * as flat keys, eliminating the need for complex nested object updates.
 */

// Settings areas are added here incrementally as they are cataloged.

// Base types and enums
const ResponseLength = z.string();

const AccountTabSectionType = createEnum([
  "wish_list",
  "recent_orders",
  "loyalty_points",
  "viewed_products",
  "product_recommendations",
]);
const ConciergeSettingsSchema_ = z.object({
  ...createPrefixedSchema("concierge/general", {
    name: z.string().nullish(),
    toneOfVoice: z.string().nullish(),
    brandDescription: z.string().nullish(),
    customerDescription: z.string().nullish(),
    outputLength: ResponseLength.nullish(),
    welcomeMessage: z.string().nullish(),
    talkingPointsKnowledgeBase: z.string().nullish(),
    enableAiWelcomeMessage: z.boolean().nullish(),
    enableTalkingPointsKb: z.boolean().nullish(),
    suggestedResponsesEnabled: z.boolean().nullish(),
    writingExamples: z.array(z.any()).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/general/channelDescriptions", {
    email: z.string().nullish(),
    live: z.string().nullish(),
    sms: z.string().nullish(),
    social: z.string().nullish(),
  }).shape,
  "concierge/enabled": z.boolean().nullish(),
  "concierge/excludedProductTags": z.array(z.string()).nullish(),
  ...createPrefixedSchema("concierge/productAvailabilityOverride", {
    enabled: z.boolean().nullish(),
    text: z.string().nullish(),
  }).shape,
  "concierge/excludedCollections": z.array(z.string()).nullish(),
  "concierge/excludedCategoryPath": z.array(z.string()).nullish(),
  "concierge/overriddenBestSellers": z.array(z.string()).nullish(),
  /** @deprecated Unread since 2026-09; replaced by product_shopify_metafield_knowledge (CRDB). Kept so stored documents validate. */
  "concierge/context/productMetafieldKeys": z.array(z.string()).nullish(),
  "concierge/placeProductFormFromThemeExtension": z.boolean().nullish(),
  "concierge/themeExtensionProductFormPlaceholder": z.string().nullish(),
  "concierge/themeExtensionProductFormButtonText": z.string().nullish(),
  "concierge/themeExtensionProductFormFontSize": z.string().nullish(),
  "concierge/themeExtensionProductFormBorderRadius": z.string().nullish(),
  "concierge/themeExtensionProductFormButtonBackgroundColor": z
    .string()
    .nullish(),
  "concierge/themeExtensionProductFormButtonTextColor": z.string().nullish(),
  "concierge/themeExtensionProductFormCustomCss": z.string().nullish(),
  "concierge/proactiveChipEngagement": z.boolean().nullish(),
  "concierge/questionPromptCount": z.number().int().min(0).nullish(),
  ...createPrefixedSchema("concierge/skills/backInStockNotifications", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/cancelOrder", {
    enabled: z.boolean().nullish(),
    cancelOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/editAddress", {
    enabled: z.boolean().nullish(),
    updateOrderAddressTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/editOrderQuantity", {
    enabled: z.boolean().nullish(),
    editOrderQuantityTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/orderTracking", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/cartEditing", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/productBundle", {
    enabled: z.boolean().nullish(),
    maxDiscountPercentage: z.number().min(0).max(10).nullish(),
    discountCodeExpirationMinutes: z.number().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/createDiscountCode", {
    enabled: z.boolean().nullish(),
    maxDiscountPercentage: z.number().min(0).nullish(),
    discountCodeExpirationMinutes: z.number().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/updateOrder", {
    enabled: z.boolean().nullish(),
    updateOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/webScrapingForProductInformation", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/forceUseRetrieveSupportKnowledge", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/productVisualization", {
    enabled: z.boolean().nullish(),
    tryItOnMessage: z.string().max(500).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/productRecommendations", {
    useShopifyRecommendations: z.boolean().nullish(),
  }).shape,
  "concierge/skills/returnsPortalLinkOverride/url": z.string().nullish(),
  ...createPrefixedSchema("concierge/emailSignUp", {
    buttonLabel: z.string().nullish(),
    incentivize: z.boolean().nullish(),
    signUpDiscountAmount: z.coerce.number().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/narrowProductRecommendation", {
    enabled: z.boolean().nullish(),
    productCount: z.number().int().min(1).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/guardrails", {
    storeContactInformation: z.string().nullish(),
  }).shape,
});

const CustomerWidgetSettingsSchema_ = z.object({
  ...createPrefixedSchema("customerWidget/general", {
    widgetColor: z.string().nullish(),
    widgetSecondaryColorEnabled: z.boolean().nullish(),
    widgetSecondaryColor: z.string().nullish(),
    hideButton: z.boolean().nullish(),
    widgetPosition: z
      .preprocess(
        (value) => {
          if (value === "bottom-left") {
            return "left";
          }
          if (value === "bottom-right") {
            return "right";
          }
          return value;
        },
        createEnum(["left", "right"]),
      )
      .nullish(),
    widgetButtonFontSize: z.coerce.number().int().min(6).max(24).nullish(),
    widgetHeaderFontSize: z.coerce.number().int().min(6).max(72).nullish(),
    widgetMessageBubbleFontSize: z.coerce
      .number()
      .int()
      .min(6)
      .max(24)
      .nullish(),
    widgetQuickActionFontSize: z.coerce.number().int().min(6).max(24).nullish(),
    widgetQuickLinkFontSize: z.coerce.number().int().min(6).max(24).nullish(),
    widgetFontFamily: z.string().nullish(),
    headingLogoUrl: z.url().nullish(),
    mainHeadingText: z.string().nullish(),
    homeHeadingText: z.string().nullish(),
    bubbleButtonText: z.string().nullish(),
    chatWithUsButtonText: z.string().nullish(),
    customCss: z.string().nullish(),
    enableInAppEmbed: z.boolean().nullish(),
    openToWelcomePage: z.boolean().nullish(),
    mobileFullscreen: z.boolean().nullish(),
    showCustomerWidgetInReturnApp: z.boolean().nullish(),
    xAdjustment: z.coerce.number().nullish(),
    yAdjustment: z.coerce.number().nullish(),
    mobileXAdjustment: z.coerce.number().nullish(),
    mobileYAdjustment: z.coerce.number().nullish(),
    zIndex: z.coerce.number().int().min(0).nullish(),
    mobileZIndex: z.coerce.number().int().min(0).nullish(),
    hideWidgetOnProductPages: z.boolean().nullish(),
    customerWidgetV2Enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/gradientSetting", {
    gradientStrength: z.number().min(0).max(1).nullish(),
    inverted: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/chips", {
    customChips: z.array(z.string()).nullish(),
    selectedPredefinedChips: z
      .array(z.nativeEnum(PredefinedChipOptions))
      .nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/selfServe", {
    editOrderLabel: z.string().nullish(),
    startClaimEnabled: z.boolean().nullish(),
    startReturnEnabled: z.boolean().nullish(),
    startReturnLabel: z.string().nullish(),
    trackPackageEnabled: z.boolean().nullish(),
    trackPackageSettingLabel: z.string().nullish(),
  }).shape,
  "customerWidget/general/widgetQuickLinks": z
    .array(z.object({ title: z.string(), url: z.url() }))
    .nullish(),

  "customerWidget/customerAccounts/accountTabSections": z
    .array(
      z.object({
        type: AccountTabSectionType,
        header: z.string(),
        enabled: z.boolean(),
      }),
    )
    .nullish(),
  "customerWidget/customerAccounts/registerHeader": z.string().nullish(),
  "customerWidget/customerAccounts/registerSubtext": z.string().nullish(),
  "customerWidget/customerAccounts/showAccountsPage": z.boolean().nullish(),
});

const AiAutopilotSettingsSchema_ = createPrefixedSchema("ai/autopilot", {
  enabled: z.boolean().nullish(),
  maxDailyTickets: z.number().int().min(0).nullish(),
  confidenceThreshold: z.number().min(0).max(1).nullish(),
  escalationKeywords: z.array(z.string()).nullish(),
  autoCloseEnabled: z.boolean().nullish(),
  autoCloseDelayHours: z.number().int().min(1).nullish(),
  approvalRequired: z.boolean().nullish(),
  approverEmails: z.array(z.email()).nullish(),
  enabledChannels: z.array(z.string()).nullish(),
});

const KnowledgeBaseSettingsSchema_ = createPrefixedSchema("knowledgeBase", {
  enabled: z.boolean().nullish(),
  autoUpdate: z.boolean().nullish(),
  sources: z
    .array(
      z.object({
        type: createEnum(["url", "file", "text"]),
        content: z.string(),
        title: z.string().nullish(),
        isActive: z.boolean(),
      }),
    )
    .nullish(),
  lastUpdated: ISODateString.nullish(),
  indexingEnabled: z.boolean().nullish(),
});

const OrderEditingSettingsSchema_ = z.object({
  "orderEditing/useOmsEvents": z.boolean().nullish(),
  "orderEditing/mergeFulfillmentsOnOrderEdit": z.boolean().nullish(),
  ...createPrefixedSchema("orderEditing/holds", {
    applyHoldToAllOrders: z.boolean().nullish(),
    holdDurationInMinutes: z.number().int().min(0).nullish(),
    holdOrdersRequiringPayment: z.boolean().nullish(),
    holdStatusStrategy: HoldStatusStrategyZod.nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/cancelOrder", {
    enabled: z.boolean().nullish(),
    cancelOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
    requireConfirmation: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/changeAddress", {
    enabled: z.boolean().nullish(),
    changeOrderAddressTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
    requireConfirmation: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/addOrderDiscount", {
    enabled: z.boolean().nullish(),
    addOrderDiscountTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/addOrderGiftNote", {
    enabled: z.boolean().nullish(),
    addOrderGiftNoteTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/updateOrder", {
    enabled: z.boolean().nullish(),
    updateOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
    addItem: z.boolean().nullish(),
    removeItem: z.boolean().nullish(),
    changeVariant: z.boolean().nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/enabledPlatforms", {
    shopperAI: z.boolean().nullish(),
    supportSelfServe: z.boolean().nullish(),
    customerPortal: z.boolean().nullish(),
    orderTracking: z.boolean().nullish(),
  }).shape,
  "orderEditing/filters": z.array(OrderEditingFilterSchema).nullish(),
  "orderEditing/rules": z.array(OrderEditingRuleSchema).nullish(),
});

const DisputesSettingsSchema_ = createPrefixedSchema("disputes", {
  enabled: z.boolean().nullish(),
  automateEvidenceEnabled: z.boolean().nullish(),
  accessOverride: createEnum(disputesAccessOverrides).nullish(),
  freeAccessEnabled: z.boolean().nullish(),
  "pricingOverride/successFeePercentage": z.number().min(0).max(100).nullish(),
  "pricingOverride/minFeeDollars": z.number().min(0).nullish(),
  "pricingOverride/source": z.string().nullish(),
  "pricingOverride/appliedAt": z.coerce.date().nullish(),
  reclaimShopifyAppInstallPromptDeferredUntil: ISODateString.nullish(),
  evidenceWindowDays: z.number().int().min(5).max(30).nullish(),
});

const LandedCostFlatSettingsSchema_ = createPrefixedSchema("landedCost", {
  enabled: z.boolean().nullish(),
  landedCostFee: z.number().nullish(),
  drawbackCoveragePercent: z.number().min(0).max(100).nullish(),
  purchaseLabelsOutsideOms: z.boolean().nullish(),
});

const LcgCommitBillingFlatSettingsSchema_ = createPrefixedSchema(
  "lcgCommitBilling",
  {
    enabled: z.boolean().nullish(),
    startDate: z.coerce.date().nullish(),
    paymentProvider: z.string().nullish(),
    billingPeriod: z.literal("week").nullish(),
    renewOnPeriodEnd: z.boolean().nullish(),
  },
);

const ReturnsFraudFlatSettingsSchema_ = createPrefixedSchema(
  "returns/fraud/zipcodes",
  {
    flaggedZips: z.array(z.string()).nullish(),
    allowlistedZips: z.array(z.string()).nullish(),
    returnRateThresholdPercent: z.coerce.number().min(0).nullish(),
    minimumOrderAmount: z.coerce.number().min(0).nullish(),
  },
);

const NewUserDefaultsSettingsSchema_ = createPrefixedSchema("newUserDefaults", {
  managedRole: createEnum(ManagedUserRoles).nullish(),
  customRoleId: zExt.objectId().nullish(),
});

// Maps to settings.returns.enableCustomerPortal; onboarding completions force it on.
const ReturnsPortalFlatSettingsSchema_ = z.object({
  "returns/enableCustomerPortal": z.boolean().nullish(),
});

const ReturnsExchangeFlatSettingsSchema_ = z.object({
  "exchanges/excessExchangeValue": createEnum([
    "store_credit",
    "refund",
    "customer_choose",
  ]).nullish(),
  ...createPrefixedSchema("exchanges/leftOverValueShopperSelectionIncentive", {
    incentivizedMethod: createEnum(["store_credit", "refund"]).nullish(),
    value: z.number().min(0).nullish(),
    currencyOverrides: z.record(z.string(), z.number().min(0)).nullish(),
    perCurrencyAmountsEnabled: z.boolean().nullish(),
  }).shape,
});

const HsaFlatSettingsSchema_ = z.object({
  ...createPrefixedSchema("hsa/buyButtons", {
    showOnlyHsaPaymentMethod: z.boolean().nullish(),
  }).shape,
});

const FulfillmentFlatSettingsSchema_ = z.object({
  ...createPrefixedSchema("outboundLabels", {
    defaultShipFromLocationExternalId: z.string().nullish(),
    disableKeyboardOnScan: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("outboundLabels/documents/packingSlip", {
    perParcelSlips: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("outboundLabels/address", {
    internationalAddressOutreach: z.boolean().nullish(),
  }).shape,
  "outboundLabels/scanVerify/manualQuantityEntry": z
    .object({
      enabled: z.boolean(),
      threshold: z.number().int().min(1).max(9999),
    })
    .nullish(),
});

export const FlatTeamSettingsSchema = z.object({
  ...SupportFlatTeamSettingsSchema.shape,
  ...ConciergeSettingsSchema_.shape,
  ...CustomerWidgetSettingsSchema_.shape,
  ...HsaFlatSettingsSchema_.shape,
  ...AiAutopilotSettingsSchema_.shape,
  ...KnowledgeBaseSettingsSchema_.shape,
  ...OrderEditingSettingsSchema_.shape,
  ...DisputesSettingsSchema_.shape,
  ...LandedCostFlatSettingsSchema_.shape,
  ...LcgCommitBillingFlatSettingsSchema_.shape,
  ...ContextualFlatTeamSettingsSchema.shape,
  ...ReturnsFlatTeamSettingsSchema.shape,
  ...ReturnsFraudFlatSettingsSchema_.shape,
  ...NewUserDefaultsSettingsSchema_.shape,
  ...ReturnsPortalFlatSettingsSchema_.shape,
  ...ReturnsExchangeFlatSettingsSchema_.shape,
  ...BrandKitFlatTeamSettingsSchema.shape,
  ...FulfillmentFlatSettingsSchema_.shape,
  ...WarrantyRegistrationFlatTeamSettingsSchema.shape,
  ...WarrantyRegistrationRewardsFlatTeamSettingsSchema.shape,
  ...CoverageFlatTeamSettingsSchema.shape,
  ...CustomerAccountsStorefrontDisplayFlatTeamSettingsSchema.shape,
  ...CustomerAccountsAdBlocksFlatTeamSettingsSchema.shape,
  ...CustomerPortalBundlesFlatTeamSettingsSchema.shape,
  ...PortalBuilderFlatTeamSettingsSchema.shape,
  ...CustomerPortalFlatTeamSettingsSchema.shape,
  ...OrderTrackingSettingsFlatTeamSettingsSchema.shape,
  ...InternationalCustomsFlatTeamSettingsSchema.shape,
  ...InternationalReturnsFlatTeamSettingsSchema.shape,
  ...ProductsGeneralFlatTeamSettingsSchema.shape,
  ...InventoryGeneralFlatTeamSettingsSchema.shape,
  ...InventoryPurchaseOrdersFlatTeamSettingsSchema.shape,
  ...ShippingFulfillmentPromiseFlatTeamSettingsSchema.shape,
  ...ShippingCarriersFlatTeamSettingsSchema.shape,
});

export type FlatTeamSettings = z.infer<typeof FlatTeamSettingsSchema>;

/**
 * Every valid flat team-setting key. Custom settings editors declare the
 * subset of these ids they render, so a typo, a removed/renamed setting, or a
 * reference to a setting that doesn't exist is a compile error rather than a
 * silently-empty editor row.
 */
export type FlatTeamSettingKey = keyof FlatTeamSettings & string;

export const FlatTeamSettingMetadataByKey = {
  ...CustomerWidgetFlatTeamSettingMetadataByKey,
  ...HsaFlatTeamSettingMetadataByKey,
  ...FulfillmentFlatTeamSettingMetadataByKey,
  ...BrandKitFlatTeamSettingMetadataByKey,
  ...ContextualFlatTeamSettingMetadataByKey,
  ...ReturnsFlatTeamSettingMetadataByKey,
  ...SupportFlatTeamSettingMetadataByKey,
  ...WarrantyRegistrationFlatTeamSettingMetadataByKey,
  ...WarrantyRegistrationRewardsFlatTeamSettingMetadataByKey,
  ...CoverageFlatTeamSettingMetadataByKey,
  ...CustomerAccountsStorefrontDisplayFlatTeamSettingMetadataByKey,
  ...CustomerAccountsAdBlocksFlatTeamSettingMetadataByKey,
  ...CustomerPortalBundlesFlatTeamSettingMetadataByKey,
  ...PortalBuilderFlatTeamSettingMetadataByKey,
  ...CustomerPortalFlatTeamSettingMetadataByKey,
  ...OrderTrackingSettingsFlatTeamSettingMetadataByKey,
  ...InternationalCustomsFlatTeamSettingMetadataByKey,
  ...InternationalReturnsFlatTeamSettingMetadataByKey,
  ...ProductsGeneralFlatTeamSettingMetadataByKey,
  ...InventoryGeneralFlatTeamSettingMetadataByKey,
  ...InventoryPurchaseOrdersFlatTeamSettingMetadataByKey,
  ...ShippingCrossBorderFlatTeamSettingMetadataByKey,
  ...ShippingFulfillmentPromiseFlatTeamSettingMetadataByKey,
  ...ShippingCarriersFlatTeamSettingMetadataByKey,
} as Partial<Record<keyof FlatTeamSettings, SettingMetadata>>;

export const FlatTeamSettingGroups = [
  ...CustomerWidgetFlatTeamSettingGroups,
  ...HsaFlatTeamSettingGroups,
  ...FulfillmentFlatTeamSettingGroups,
  ...BrandKitFlatTeamSettingGroups,
  ...ContextualFlatTeamSettingGroups,
  ...ReturnsFlatTeamSettingGroups,
  ...SupportFlatTeamSettingGroups,
  ...WarrantyRegistrationFlatTeamSettingGroups,
  ...WarrantyRegistrationRewardsFlatTeamSettingGroups,
  ...CoverageFlatTeamSettingGroups,
  ...CustomerAccountsStorefrontDisplayFlatTeamSettingGroups,
  ...CustomerAccountsAdBlocksFlatTeamSettingGroups,
  ...CustomerPortalBundlesFlatTeamSettingGroups,
  ...PortalBuilderFlatTeamSettingGroups,
  ...CustomerPortalFlatTeamSettingGroups,
  ...OrderTrackingSettingsFlatTeamSettingGroups,
  ...InternationalCustomsFlatTeamSettingGroups,
  ...InternationalReturnsFlatTeamSettingGroups,
  ...ProductsGeneralFlatTeamSettingGroups,
  ...InventoryGeneralFlatTeamSettingGroups,
  ...InventoryPurchaseOrdersFlatTeamSettingGroups,
  ...ShippingCrossBorderFlatTeamSettingGroups,
  ...ShippingFulfillmentPromiseFlatTeamSettingGroups,
  ...ShippingCarriersFlatTeamSettingGroups,
] as const satisfies readonly SettingGroupDefinition[];

export function getFlatTeamSettingMetadata(
  key: keyof FlatTeamSettings,
): SettingMetadata | undefined {
  return FlatTeamSettingMetadataByKey[key];
}

export function getFlatTeamSettingGroup(
  groupId: string,
): SettingGroupDefinition | undefined {
  return FlatTeamSettingGroups.find((group) => group.id === groupId);
}

/**
 * Keys the caller may not write. A key with no catalog entry is treated as
 * writable, which is what every key does today — declaring access is opt-in.
 */
export function findSettingsCallerCannotWrite({
  keys,
  viewerVisibility,
  permissions,
}: {
  keys: readonly (keyof FlatTeamSettings)[];
  viewerVisibility: SettingVisibility;
  permissions: readonly string[];
}): (keyof FlatTeamSettings)[] {
  return keys.filter((key) => {
    const metadata = getFlatTeamSettingMetadata(key);
    if (!metadata) {
      return false;
    }
    return !canDirectlyWriteSetting({
      viewerVisibility,
      permissions,
      access: metadata.access,
    });
  });
}

export function getFlatTeamSettingKeysForGroup(
  groupId: string,
): (keyof FlatTeamSettings)[] {
  return [
    ...CustomerWidgetFlatTeamSettingCatalogDefinitions,
    ...HsaFlatTeamSettingCatalogDefinitions,
    ...FulfillmentFlatTeamSettingCatalogDefinitions,
    ...ReturnsFlatTeamSettingCatalogDefinitions,
    ...SupportFlatTeamSettingCatalogDefinitions,
    ...WarrantyRegistrationFlatTeamSettingCatalogDefinitions,
    ...WarrantyRegistrationRewardsFlatTeamSettingCatalogDefinitions,
    ...CoverageFlatTeamSettingCatalogDefinitions,
    ...CustomerAccountsStorefrontDisplayFlatTeamSettingCatalogDefinitions,
    ...CustomerAccountsAdBlocksFlatTeamSettingCatalogDefinitions,
    ...CustomerPortalBundlesFlatTeamSettingCatalogDefinitions,
    ...PortalBuilderFlatTeamSettingCatalogDefinitions,
    ...CustomerPortalFlatTeamSettingCatalogDefinitions,
    ...OrderTrackingSettingsFlatTeamSettingCatalogDefinitions,
    ...InternationalCustomsFlatTeamSettingCatalogDefinitions,
    ...InternationalReturnsFlatTeamSettingCatalogDefinitions,
    ...ProductsGeneralFlatTeamSettingCatalogDefinitions,
    ...InventoryGeneralFlatTeamSettingCatalogDefinitions,
    ...InventoryPurchaseOrdersFlatTeamSettingCatalogDefinitions,
    ...ShippingCrossBorderFlatTeamSettingCatalogDefinitions,
    ...ShippingFulfillmentPromiseFlatTeamSettingCatalogDefinitions,
    ...ShippingCarriersFlatTeamSettingCatalogDefinitions,
  ]
    .filter((definition) => {
      const metadata = definition.metadata;
      return (
        definition.groupId === groupId ||
        metadata.relatedGroups?.includes(groupId)
      );
    })
    .map((definition) => definition.id as keyof FlatTeamSettings);
}

export function getFlatTeamSettingGroupsForProductArea(
  productArea: SettingProductArea,
): readonly SettingGroupDefinition[] {
  return FlatTeamSettingGroups.filter(
    (group) => group.productArea === productArea,
  );
}

export const FlatTeamSettingKeys = Object.keys(
  FlatTeamSettingsSchema.shape,
) as (keyof FlatTeamSettings)[];

export const ValidFlatSettingKeysSchema = z.array(
  createEnum(
    FlatTeamSettingKeys as [
      keyof FlatTeamSettings,
      ...(keyof FlatTeamSettings)[],
    ],
  ),
);

/**
 * Utility function to create a schema for a subset of keys
 */
export function createPickedFlatSettingsSchema<
  K extends keyof FlatTeamSettings,
>(keys: readonly K[]): z.ZodType<Pick<FlatTeamSettings, K>> {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  for (const key of keys) {
    const keySchema = FlatTeamSettingsSchema.shape[key];
    if (keySchema) {
      schemaShape[key] = keySchema;
    } else {
      throw new Error(`Unknown setting key: ${key}`);
    }
  }

  return z.object(schemaShape) as unknown as z.ZodType<
    Pick<FlatTeamSettings, K>
  >;
}

const UpdateFlatTeamSettingsSchema = zExt.optionalToNullable(
  FlatTeamSettingsSchema,
);
export type UpdateFlatTeamSettings = z.infer<
  typeof UpdateFlatTeamSettingsSchema
>;

export function createPickedUpdateFlatSettingsSchema<
  K extends keyof FlatTeamSettings,
>(keys: readonly K[]): z.ZodType<Pick<UpdateFlatTeamSettings, K>> {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  for (const key of keys) {
    const keySchema = UpdateFlatTeamSettingsSchema.shape[key];
    if (keySchema) {
      schemaShape[key] = keySchema;
    } else {
      throw new Error(`Unknown setting key: ${key}`);
    }
  }

  return z.object(schemaShape) as unknown as z.ZodType<
    Pick<UpdateFlatTeamSettings, K>
  >;
}

// Type guard for checking if a key exists in the flat settings schema
export function isFlatSettingKey(key: string): key is keyof FlatTeamSettings {
  return key in FlatTeamSettingsSchema.shape;
}

/**
 * Utility to get the schema for a specific key
 */
export function getSchemaForKey(
  key: keyof FlatTeamSettings,
): z.ZodTypeAny | undefined {
  return FlatTeamSettingsSchema.shape[key];
}

export const FlatTeamSettingsPartialRecordSchema = z.partialRecord(
  z.keyof(FlatTeamSettingsSchema),
  z.literal(1),
);
export type FlatTeamSettingsPartialRecord = z.infer<
  typeof FlatTeamSettingsPartialRecordSchema
>;
